import { Player, SEAT_COLORS } from './Player.js';

/**
 * Roster Model managing active players, teams, circular seat order, and display names.
 */
export class Roster {
  constructor(options = {}) {
    this.playerCount = Number(options.playerCount || 2);
    this.gameMode = options.gameMode || 'solo';
    this.players = new Map();
    this.init(options);
  }

  static normalizeSeat(seatId) {
    if (!seatId) return 'p1';
    if (typeof seatId === 'object') {
      return (seatId.id && typeof seatId.id === 'string' && !seatId.id.includes('[object')) ? seatId.id : 'p1';
    }
    if (seatId === 'you') return 'p1';
    if (seatId === 'cpu') return 'p2';
    const str = String(seatId);
    return str.includes('[object') ? 'p1' : str;
  }

  init(options = {}) {
    this.players.clear();
    const count = this.playerCount;
    const isHvH = options.isHvH || false;
    const isOnline = options.isOnline || false;

    if (Array.isArray(options.seats) && options.seats.length > 0) {
      for (const s of options.seats.slice(0, count)) {
        if (!s) continue;
        const sId = Roster.normalizeSeat(s.id);
        const player = (s instanceof Player) ? s : new Player({
          ...s,
          id: sId,
          team: s.team || this.getTeamForSeat(sId, count, this.gameMode),
          color: s.color || SEAT_COLORS[sId]
        });
        this.players.set(sId, player);
      }
      return;
    }

    for (let i = 1; i <= count; i++) {
      const sId = `p${i}`;
      let defaultName = `Player ${i}`;
      let type = 'human';

      if (i === 1) {
        defaultName = isHvH ? 'Player 1' : 'You';
      } else if (i === 2) {
        defaultName = isHvH ? 'Player 2' : (isOnline ? 'Opponent' : 'Computer');
        type = isHvH || isOnline ? 'human' : 'cpu';
      }

      this.players.set(sId, new Player({
        id: sId,
        name: (options.names && options.names[sId]) || defaultName,
        type: (options.types && options.types[sId]) || type,
        team: this.getTeamForSeat(sId, count, this.gameMode),
        color: SEAT_COLORS[sId]
      }));
    }
  }

  static fromSeats(seats, options = {}) {
    const count = (options && options.playerCount) || (Array.isArray(seats) ? seats.length : 2);
    return new Roster({ ...options, playerCount: count, seats });
  }

  getPlayer(seatId) {
    const sId = Roster.normalizeSeat(seatId);
    return this.players.get(sId) || null;
  }

  getActiveSeatIds() {
    return Array.from(this.players.keys());
  }

  getActivePlayers() {
    return Array.from(this.players.values());
  }

  getNextSeat(fromSeatId) {
    const ids = this.getActiveSeatIds();
    if (!ids.length) return 'p1';
    const current = Roster.normalizeSeat(fromSeatId);
    const idx = ids.indexOf(current);
    if (idx < 0) return ids[0];
    return ids[(idx + 1) % ids.length];
  }

  isPartnership() {
    return ['pairs', 'trios', 'quads'].includes(this.gameMode) || this.playerCount >= 4;
  }

  getTeamForSeat(seatId, count = this.playerCount, mode = this.gameMode) {
    const sId = Roster.normalizeSeat(seatId);
    if (!['pairs', 'trios', 'quads'].includes(mode) && count === 2) {
      return sId;
    }
    if (count === 3) {
      return sId === 'p1' ? 'team1' : 'team2';
    }
    if (count === 4) {
      return (sId === 'p1' || sId === 'p3') ? 'team1' : 'team2';
    }
    if (count === 5) {
      if (sId === 'p1') return 'team1';
      if (sId === 'p2' || sId === 'p4') return 'team2';
      return 'team3';
    }
    if (count === 6) {
      if (sId === 'p1' || sId === 'p4') return 'team1';
      if (sId === 'p2' || sId === 'p5') return 'team2';
      return 'team3';
    }
    if (count === 8) {
      if (sId === 'p1' || sId === 'p5') return 'team1';
      if (sId === 'p2' || sId === 'p6') return 'team2';
      if (sId === 'p3' || sId === 'p7') return 'team3';
      return 'team4';
    }
    return sId;
  }

  getSeatName(seatId) {
    if (!seatId) return 'Player';
    if (seatId === 'crib') return 'The Crib';
    const sId = Roster.normalizeSeat(seatId);
    const p = this.players.get(sId);
    return (p && p.name) || sId.toUpperCase();
  }

  getPlayerDisplayName(who) {
    if (!who) return 'Player';
    if (who === 'team1') {
      if (this.playerCount === 4) return 'Team 1 (P1 & P3)';
      if (this.playerCount === 6) return 'Team 1 (P1 & P4)';
      if (this.playerCount === 8) return 'Team 1 (P1 & P5)';
      return 'Team 1';
    }
    if (who === 'team2') {
      if (this.playerCount === 3) return `Pair (${this.getSeatName('p2')} & ${this.getSeatName('p3')})`;
      if (this.playerCount === 4) return 'Team 2 (P2 & P4)';
      if (this.playerCount === 6) return 'Team 2 (P2 & P5)';
      if (this.playerCount === 8) return 'Team 2 (P2 & P6)';
      return 'Team 2';
    }
    if (who === 'team3') {
      if (this.playerCount === 6) return 'Team 3 (P3 & P6)';
      if (this.playerCount === 8) return 'Team 3 (P3 & P7)';
      return 'Team 3';
    }
    if (who === 'team4') {
      return 'Team 4 (P4 & P8)';
    }
    return this.getSeatName(who);
  }

  static seatName(seatId, context = {}) {
    if (!seatId) return 'Player';
    if (typeof seatId === 'object' && !seatId.id) return 'Player';
    if (seatId === 'crib') return 'The Crib';
    const sId = Roster.normalizeSeat(seatId);

    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const getSeat = context.getSeat || (typeof window !== 'undefined' && window.getSeat ? window.getSeat : Roster.getSeat);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const isHvH = context.isHvH || (typeof window !== 'undefined' ? window.isHvH : () => false);

    const s = getSeat(sId, context);
    if (isOnline()) {
      if (sId === mySeat()) {
        return (s && s.name && s.name !== 'You' && s.name !== 'Guest') ? `${s.name} (You)` : 'You';
      }
      return (s && s.name) ? s.name : 'Opponent';
    }
    if (!settings || (parseInt(settings.playerCount, 10) || 2) === 2) {
      if (sId === 'p1') return (s && s.name && s.name !== 'You') ? s.name : (isHvH() ? 'Player 1' : 'You');
      if (sId === 'p2') return (s && s.name && s.name !== 'Player 2' && s.name !== 'cpu' && s.name !== 'Computer') ? s.name : (isHvH() ? 'Player 2' : ((s && s.name) || 'Computer'));
    }
    return (s && s.name) || (typeof sId === 'string' ? sId.toUpperCase() : 'Player');
  }

  static pName(who, context = {}) {
    if (!who) return 'Player';
    const seatName = context.seatName || (typeof window !== 'undefined' && typeof window.seatName === 'function' ? window.seatName : Roster.seatName);
    const isSoloVsPairs = context.isSoloVsPairs || (typeof window !== 'undefined' ? window.isSoloVsPairs : () => false);
    const is3Player1vs2 = context.is3Player1vs2 || (typeof window !== 'undefined' ? window.is3Player1vs2 : () => false);
    const is5Player1vs4 = context.is5Player1vs4 || (typeof window !== 'undefined' ? window.is5Player1vs4 : () => false);
    const is7Player1vs6 = context.is7Player1vs6 || (typeof window !== 'undefined' ? window.is7Player1vs6 : () => false);
    const getPlayerCount = context.getPlayerCount || (typeof window !== 'undefined' ? window.getPlayerCount : () => 2);

    if (who === 'team1') {
      if (isSoloVsPairs()) return `${seatName('p1', context)} (Solo)`;
      const count = getPlayerCount();
      if (count === 4) return 'Team 1 (P1 & P3)';
      if (count === 6) return 'Team 1 (P1 & P4)';
      if (count === 8) return 'Team 1 (P1 & P5)';
      return 'Team 1';
    }
    if (who === 'team2') {
      if (is3Player1vs2()) return `Pair (${seatName('p2', context)} & ${seatName('p3', context)})`;
      if (is5Player1vs4()) return `Pair 1 (${seatName('p2', context)} & ${seatName('p4', context)})`;
      if (is7Player1vs6()) return `Pair 1 (${seatName('p2', context)} & ${seatName('p5', context)})`;
      const count = getPlayerCount();
      if (count === 4) return 'Team 2 (P2 & P4)';
      if (count === 6) return 'Team 2 (P2 & P5)';
      if (count === 8) return 'Team 2 (P2 & P6)';
      return 'Team 2';
    }
    if (who === 'team3') {
      if (is5Player1vs4()) return `Pair 2 (${seatName('p3', context)} & ${seatName('p5', context)})`;
      if (is7Player1vs6()) return `Pair 2 (${seatName('p3', context)} & ${seatName('p6', context)})`;
      const count = getPlayerCount();
      if (count === 6) return 'Team 3 (P3 & P6)';
      if (count === 8) return 'Team 3 (P3 & P7)';
      return 'Team 3';
    }
    if (who === 'team4') {
      if (is7Player1vs6()) return `Pair 3 (${seatName('p4', context)} & ${seatName('p7', context)})`;
      return 'Team 4 (P4 & P8)';
    }
    return seatName(who, context);
  }

  static getSeatTeam(seatId, context = {}) {
    const sId = Roster.normalizeSeat(seatId);
    const is3Player1vs2 = context.is3Player1vs2 || (typeof window !== 'undefined' ? window.is3Player1vs2 : () => false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);

    if (is3Player1vs2()) {
      return sId === 'p1' ? 'team1' : 'team2';
    }
    const count = (settings && settings.playerCount) || 2;
    if (count === 5 && settings && settings.gameMode === 'shifting') {
      if (sId === 'p1') return 'team1';
      if (sId === 'p2' || sId === 'p4') return 'team2';
      if (sId === 'p3' || sId === 'p5') return 'team3';
    }
    if (count === 7 && settings && settings.gameMode === 'shifting') {
      if (sId === 'p1') return 'team1';
      if (sId === 'p2' || sId === 'p5') return 'team2';
      if (sId === 'p3' || sId === 'p6') return 'team3';
      if (sId === 'p4' || sId === 'p7') return 'team4';
    }
    if (count === 4) {
      if (sId === 'p1' || sId === 'p3') return 'team1';
      if (sId === 'p2' || sId === 'p4') return 'team2';
    } else if (count === 6) {
      if (sId === 'p1' || sId === 'p4') return 'team1';
      if (sId === 'p2' || sId === 'p5') return 'team2';
      if (sId === 'p3' || sId === 'p6') return 'team3';
    } else if (count === 8) {
      if (sId === 'p1' || sId === 'p5') return 'team1';
      if (sId === 'p2' || sId === 'p6') return 'team2';
      if (sId === 'p3' || sId === 'p7') return 'team3';
      if (sId === 'p4' || sId === 'p8') return 'team4';
    }
    return (sId === 'p1' || sId === 'p3' || sId === 'p5' || sId === 'p7') ? 'team1' : 'team2';
  }

  static getSeat(seatId, context = {}) {
    let sId = Roster.normalizeSeat(seatId);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings && Array.isArray(settings.seats)) {
      const found = settings.seats.find(s => s && s.id === sId);
      if (found && found.name) return found;
    }
    const getRoster = context.getRoster || (typeof window !== 'undefined' ? window.getRoster : null);
    const r = (typeof getRoster === 'function') ? getRoster() : null;
    if (r && typeof r.getPlayer === 'function') {
      const p = r.getPlayer(sId);
      if (p) return p;
    }
    return ((settings && settings.seats) || []).find(s => s && s.id === sId) || { id: sId, name: sId, type: 'cpu' };
  }

  static prevSeat(seatId, activeSeats = null) {
    const sId = Roster.normalizeSeat(seatId);
    const active = activeSeats || (typeof window !== 'undefined' && typeof window.getActiveSeatIds === 'function' ? window.getActiveSeatIds() : ['p1', 'p2']);
    const idx = active.indexOf(sId);
    if (idx < 0) return active[0] || 'p1';
    return active[(idx - 1 + active.length) % active.length];
  }

  static seatColor(seatId) {
    if (seatId === 'p1' || seatId === 'you' || seatId === 'team1') return '#d4a017';
    if (seatId === 'p2' || seatId === 'cpu' || seatId === 'team2') return '#ef4444';
    if (seatId === 'p3') return '#10b981';
    if (seatId === 'p4') return '#3b82f6';
    if (seatId === 'p5') return '#a855f7';
    if (seatId === 'p6') return '#f97316';
    if (seatId === 'p7' || seatId === 'team3') return '#06b6d4';
    if (seatId === 'p8' || seatId === 'team4') return '#ec4899';
    return '#d4a017';
  }

  static getShiftingRoles(context = {}) {
    const isShiftingPartnership = context.isShiftingPartnership || (typeof window !== 'undefined' ? window.isShiftingPartnership : () => false);
    if (!isShiftingPartnership()) return null;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => []);
    const active = getActiveSeatIds();
    const d = (state && state.dealer) ? state.dealer : active[0];
    const dIdx = active.indexOf(d);
    const others = [];
    for (let i = 1; i < active.length; i++) {
      others.push(active[(dIdx + i) % active.length]);
    }
    const half = others.length / 2;
    const pairs = [];
    for (let i = 0; i < half; i++) {
      pairs.push([others[i], others[i + half]]);
    }
    return { lone: d, pairs };
  }

  static getShiftingPartner(seatId, context = {}) {
    const getShiftingRoles = context.getShiftingRoles || Roster.getShiftingRoles;
    const roles = getShiftingRoles(context);
    if (!roles) return null;
    let sId = Roster.normalizeSeat(seatId);
    if (sId === roles.lone) return null;
    for (const pair of roles.pairs) {
      if (pair[0] === sId) return pair[1];
      if (pair[1] === sId) return pair[0];
    }
    return null;
  }

  static isTwoHuman(context = {}) {
    const isHvH = context.isHvH || (typeof window !== 'undefined' && window.isHvH ? window.isHvH : () => false);
    const isOnline = context.isOnline || (typeof window !== 'undefined' && window.isOnline ? window.isOnline : () => false);
    return isHvH() || isOnline();
  }

  static isSoloVsPairs(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    if (!settings) return false;
    const pCount = parseInt(settings.playerCount, 10) || 2;
    return settings.gameMode === 'shifting' && (pCount === 3 || pCount === 5 || pCount === 7);
  }

  static is3Player1vs2(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    if (!settings) return false;
    const pCount = parseInt(settings.playerCount, 10) || 2;
    return pCount === 3 && (settings.gameMode === 'shifting' || settings.gameMode === '1vs2');
  }

  static is5Player1vs4(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    if (!settings) return false;
    const pCount = parseInt(settings.playerCount, 10) || 2;
    return pCount === 5 && settings.gameMode === 'shifting';
  }

  static is7Player1vs6(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    if (!settings) return false;
    const pCount = parseInt(settings.playerCount, 10) || 2;
    return pCount === 7 && settings.gameMode === 'shifting';
  }

  static isFixedTeams(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    if (!settings) return false;
    const pCount = parseInt(settings.playerCount, 10) || 2;
    return Roster.isSoloVsPairs(settings) ||
           Boolean(settings.optCanadianDoubles && pCount === 4) ||
           (pCount === 4 && settings.gameMode === 'partnership') ||
           (pCount === 6 && settings.gameMode === 'teams') ||
           (pCount === 8 && settings.gameMode === 'teams');
  }

  static isPartnership(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    return Roster.isFixedTeams(settings);
  }

  static isShiftingPartnership(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    return false;
  }

  static mySeat(context = {}) {
    const isOnline = context.isOnline || (typeof window !== 'undefined' && window.isOnline ? window.isOnline : () => false);
    const net = context.net || (typeof window !== 'undefined' ? window.net : null);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (isOnline()) {
      return (net && net.mySeatId) || (settings && settings.netRole === 'guest' ? 'p2' : 'p1');
    }
    return 'p1';
  }

  static getPlayerCount(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    return (settings && settings.playerCount) || 2;
  }

  static getActiveSeats(settings = (typeof window !== 'undefined' ? window.settings : null)) {
    const count = Roster.getPlayerCount(settings);
    if (settings && Array.isArray(settings.seats)) {
      return settings.seats.slice(0, count);
    }
    if (settings && settings.seats && typeof settings.seats === 'object') {
      return Object.values(settings.seats).slice(0, count);
    }
    return [];
  }

  static getActiveSeatIds(context = {}) {
    const r = (typeof context.getRoster === 'function' ? context.getRoster() : null) ||
              (typeof window !== 'undefined' && typeof window.getRoster === 'function' ? window.getRoster() : null);
    if (r && typeof r.getActiveSeatIds === 'function') {
      return r.getActiveSeatIds();
    }
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    return Roster.getActiveSeats(settings).map(s => s.id);
  }

  static isSeatCpu(seatId, context = {}) {
    if (!seatId || typeof seatId !== 'string') return false;
    let sId = Roster.normalizeSeat(seatId);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings && Array.isArray(settings.seats)) {
      const s = settings.seats.find(x => x && x.id === sId);
      if (s && s.type) return s.type === 'cpu';
    } else if (settings && settings.seats && typeof settings.seats === 'object') {
      const s = settings.seats[sId];
      if (s && s.type) return s.type === 'cpu';
    }
    const isTwoHuman = context.isTwoHuman || (typeof window !== 'undefined' && window.isTwoHuman ? window.isTwoHuman : () => false);
    if (!settings || (parseInt(settings.playerCount, 10) || 2) === 2) {
      return !isTwoHuman() && sId === 'p2';
    }
    const getSeat = context.getSeat || (typeof window !== 'undefined' && window.getSeat ? window.getSeat : Roster.getSeat);
    const s = getSeat(sId, context);
    return s ? s.type === 'cpu' : false;
  }

  static isSeatHuman(seatId, context = {}) {
    if (!seatId || typeof seatId !== 'string') return false;
    return !Roster.isSeatCpu(seatId, context);
  }

  static scoreKey(who, context = {}) {
    const isFixedTeams = context.isFixedTeams || Roster.isFixedTeams;
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (isFixedTeams(settings)) {
      const getSeatTeam = context.getSeatTeam || (typeof window !== 'undefined' && window.getSeatTeam ? window.getSeatTeam : Roster.getSeatTeam);
      return getSeatTeam(who, context);
    }
    if (who === 'you') return 'p1';
    if (who === 'cpu') return 'p2';
    return who;
  }

  static getRoster(context = {}) {
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null);
    const count = Roster.getPlayerCount(settings);
    const mode = (settings && settings.gameMode) || 'solo';
    const win = typeof window !== 'undefined' ? window : {};
    if (!win._currentRoster || win._currentRoster.playerCount !== count || win._currentRoster.gameMode !== mode) {
      const isHvHMode = typeof context.isHvH === 'function' ? context.isHvH() : (typeof win.isHvH === 'function' ? win.isHvH() : false);
      const isOnlineMode = typeof context.isOnline === 'function' ? context.isOnline() : (typeof win.isOnline === 'function' ? win.isOnline() : false);
      const seats = (settings && settings.seats) || [];
      win._currentRoster = new Roster({
        playerCount: count,
        gameMode: mode,
        isHvH: isHvHMode,
        isOnline: isOnlineMode,
        seats
      });
    }
    return win._currentRoster;
  }
}
